import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  signal,
  Signal,
} from '@angular/core';
import { IBankData, ICreateBankData } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom, map } from 'rxjs';
import { BankDataGateway } from './bank-data.gateway';

@Injectable({
  providedIn: 'root',
})
export class BankDataStore {
  private readonly gateway: BankDataGateway = new BankDataGateway();
  private readonly shouldLoad = signal(false);

  #bankDataResource: ResourceRef<IApiResponse<IBankData[]> | undefined> =
    resource({
      params: () => this.shouldLoad() || undefined,
      loader: (): Promise<IApiResponse<IBankData[]> | undefined> => {
        return firstValueFrom(this.gateway.list());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#bankDataResource.isLoading()
  );
  readonly bankData: Signal<IBankData[]> = computed((): IBankData[] =>
    this.#bankDataResource.hasValue()
      ? (this.#bankDataResource.value()?.data ?? [])
      : []
  );

  getBankData(): void {
    if (this.shouldLoad()) {
      this.reload();
      return;
    }
    this.shouldLoad.set(true);
  }

  async addBankData(
    newBankData: ICreateBankData
  ): Promise<IBankData[] | undefined> {
    return await firstValueFrom(
      this.gateway
        .save(newBankData)
        .pipe(map((response: IApiResponse<IBankData[]>) => response.data))
    );
  }

  reload(): void {
    this.#bankDataResource.reload();
  }
}
